import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import type { SelectOption } from '@/components/Select/Select'
import Filters, {
  type Filter,
  type FiltersProps
} from '@/components/common/Filters/Filters'
import { useAllEducationLevels } from '@/queries/useAllEducationLevels'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'

import type {
  CoursesManagementFilters,
  CoursesManagementFiltersKey
} from '../constants/courses-management-filters'
import { coursesQueryFilterOptions } from '../queries/coursesFilters/coursesQueryFilterOptions'
import { domainsQueryFilterOptions } from '../queries/coursesFilters/domainsQueryFilterOptions'
import { roomsQueryFilterOptions } from '../queries/coursesFilters/roomsQueryFilterOptions'

type CoursesManagementListFiltersProps = {
  filters: Omit<CoursesManagementFilters, 'page' | 'pageSize'>
  onFilterChange: (
    key: CoursesManagementFiltersKey,
    value?: SelectOption<string>[] | string[]
  ) => void
} & Pick<FiltersProps, 'onClearAll'>

const CoursesManagementListFilters = (
  props: CoursesManagementListFiltersProps
) => {
  const { t } = useTranslation('coursesManagement')
  const { data: allEducationLevels, isLoading: areAllEducationLevelsLoading } =
    useAllEducationLevels()

  const filtersArr: Filter[] = [
    {
      label: t('label.course-name'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'course-name-filter',
        multiple: true,
        queryOptions: coursesQueryFilterOptions,
        value: props.filters.ids,
        placeholder: t('label.course-name'),
        onChange: value => props.onFilterChange('ids', value)
      }
    },
    {
      label: t('label.education-level'),
      variant: 'multiselect',
      filterProps: {
        id: 'education-level-filter',
        multiple: true,
        options: allEducationLevels?.options ?? [],
        loading: areAllEducationLevelsLoading,
        value: props.filters.educationLevelIds,
        placeholder: t('label.education-level'),
        onChange: value => props.onFilterChange('educationLevelIds', value)
      }
    },
    {
      label: t('label.domain'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'domain-name-filter',
        multiple: true,
        queryOptions: domainsQueryFilterOptions,
        value: props.filters.domainIds,
        placeholder: t('label.domain'),
        onChange: value => props.onFilterChange('domainIds', value)
      }
    },
    {
      label: t('label.preferred-rooms'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'preferred-room-filter',
        multiple: true,
        queryOptions: roomsQueryFilterOptions,
        value: props.filters.preferredRoomIds,
        placeholder: t('label.preferred-rooms'),
        onChange: value => props.onFilterChange('preferredRoomIds', value)
      }
    }
  ]

  const isFilterSelected = checkIfFilterSelected(filtersArr)

  return (
    <Filters
      filters={filtersArr}
      actionsRender={
        <Button asLink to="/courses-management/add" icon={<AddIcon />}>
          {t('button.add-new-course')}
        </Button>
      }
      disabledClearAllButton={!isFilterSelected}
      onClearAll={props.onClearAll}
    />
  )
}

export default CoursesManagementListFilters
